//dashboard/_layout.tsx
import { Stack, useRouter, usePathname } from 'expo-router';
import { useEffect } from 'react';
import { Pressable, View } from 'react-native';

import { PaywallStatusProvider, usePaywallStatusContext } from '@/src/hooks/usePaywallStatus';
import { getItem } from '@/src/lib/storage';
import HonestPaywallBanner from '@/components/paywall/HonestPaywallBanner';

const PAYWALL_SCREEN = '/dashboard/paywall-expired';
const SELF_PAY_SCREEN = '/dashboard/self-pay';

function DashboardLayoutContent() {
  const router = useRouter();
  const pathname = usePathname();
  const paywall = usePaywallStatusContext();
  const status = paywall?.status ?? null;

  useEffect(() => {
    const verifyAuth = async () => {
      const token = await getItem('userToken');
      if (!token) {
        router.replace('/');
      }
    };
    verifyAuth();
  }, [pathname, router]);

  // D-0 gate (#209, Q2 d0_behavior="block"): owner with an expired trial is
  // hard-blocked into the paywall screen; a driver past the company grace
  // window (Q6) too. Checked on mount and on every foreground via the hook.
  useEffect(() => {
    // self-pay is the driver's way OUT of the block (#207) — never bounce it.
    if (!status || pathname === PAYWALL_SCREEN || pathname === SELF_PAY_SCREEN) return;
    const ownerBlocked = status.state === 'trial_expired' && status.is_owner;
    const driverBlocked = status.state === 'blocked' && !status.is_owner;
    if (ownerBlocked || driverBlocked) {
      router.replace(PAYWALL_SCREEN);
    }
  }, [status, pathname, router]);

  // Honest paywall invariant: the banner is visible on EVERY dashboard
  // screen during the trial (and during the company grace, for drivers).
  const showTrialBanner =
    status?.state === 'trialing' && status.days_remaining !== null && status.trial_days !== null;
  const showGraceBanner = status?.state === 'company_grace';

  return (
    <View style={{ flex: 1 }}>
      {showTrialBanner && (
        <HonestPaywallBanner
          trialDays={status!.trial_days!}
          daysRemaining={status!.days_remaining!}
          isOwner={status!.is_owner}
        />
      )}
      {showGraceBanner && (
        // During the grace the banner doubles as the self-pay CTA (#207):
        // tapping it opens the "pay my own license" screen.
        <Pressable
          onPress={() => router.push(SELF_PAY_SCREEN)}
          accessibilityRole="button"
          testID="grace-banner-cta"
        >
          <HonestPaywallBanner
            trialDays={0}
            daysRemaining={0}
            isCompanyGrace
            graceDaysRemaining={status!.grace_days_remaining ?? 0}
            isOwner={false}
          />
        </Pressable>
      )}
      <Stack screenOptions={{ headerShown: false }}>
        <Stack.Screen name="home" options={{ headerShown: false }} />
        <Stack.Screen name="user-data" options={{ title: "User Data" }} />
        {/* Add other dashboard routes here */}
      </Stack>
    </View>
  );
}

export default function DashboardLayout() {
  return (
    <PaywallStatusProvider>
      <DashboardLayoutContent />
    </PaywallStatusProvider>
  );
}
